import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from "react-toastify";
import { API_BASE_URL } from "../../config/apiConfig";
const AIGenerateImageModal = ({ 
  isOpen, 
  onClose, 
  projectId, 
  onImageGenerated 
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [projectData, setProjectData] = useState(null);

  // Fetch current project data when modal opens
  useEffect(() => {
    if (isOpen && projectId) {
      fetchProjectData();
    }
  }, [isOpen, projectId]);

  const fetchProjectData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/frond/${projectId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      setProjectData(response.data?.response?.data);
    } catch (error) {
      console.error('Failed to fetch project data:', error);
      toast.error('Failed to load project data');
    }
  };

  const generateImages = async () => {
    setIsGenerating(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/projects/${projectId}/ai-images/generate`,
        {
          style: 'modern',
          mood: 'professional'
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setGeneratedImages(response.data.data.images);
        toast.success('Images generated successfully!');
      }
    } catch (error) {
      console.error('Generation failed:', error);
      toast.error(error.response?.data?.message || 'Failed to generate images');
    } finally {
      setIsGenerating(false);
    }
  };

  const selectAsCover = async (image) => {
    if (!projectData) {
      toast.error('Project data not loaded. Please try again.');
      return;
    }

    setSelectedImage(image);
    setSaving(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // First, download the image from Unsplash URL
      const imageResponse = await fetch(image.url);
      const blob = await imageResponse.blob();
      
      // Create a File object from the blob
      const file = new File([blob], `ai-cover-${Date.now()}.jpg`, { type: 'image/jpeg' });
      
      // Create FormData
      const formData = new FormData();
      
      // Add the image file
      formData.append('image', file);
      
      // Add ALL required fields from existing project data
      formData.append('title', projectData?.frond?.title || '');
      formData.append('category', projectData?.frond?.category || '');
      formData.append('desc', projectData?.frond?.desc || '');
      formData.append('budget', projectData?.frond?.budget || '');
      formData.append('currency', projectData?.frond?.currency || '');
      formData.append('priority', projectData?.frond?.priority || '');
      formData.append('location', projectData?.frond?.location || '');
      formData.append('longitude', projectData?.longitude || '');
      formData.append('latitude', projectData?.latitude || '');
      formData.append('country', projectData?.frond?.country || '');
      formData.append('city', projectData?.frond?.city || '');
      
      // Handle dates (if they exist)
      if (projectData?.frond?.start_date) {
        formData.append('start_date', projectData.frond.start_date);
      }
      if (projectData?.frond?.end_date) {
        formData.append('end_date', projectData.frond.end_date);
      }
      
      // Handle tags (if they exist)
      if (projectData?.tags && projectData.tags.length > 0) {
        projectData.tags.forEach((tag) => {
          formData.append('tags[]', tag.name.en);
        });
      }
      
      // Send the update request
      const response = await axios.post(
        `${API_BASE_URL}/frond-about/${projectId}?_method=PUT`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      toast.success('Image set as project cover successfully!');
      
      // Refresh the page to show new image
      if (onImageGenerated) onImageGenerated();
      
      // Close modal after short delay
      setTimeout(() => {
        onClose();
      }, 1500);
      
    } catch (error) {
      console.error('Failed to set image:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.message || 'Failed to set image as cover';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex justify-center items-center z-[1000] animate-fadeIn">
      <div className="bg-white rounded-2xl max-w-4xl w-[90%] max-h-[85vh] overflow-hidden shadow-2xl animate-slideUp">
        {/* Header */}
        <div className="bg-gradient-to-r to-orange-500 from-pink-500 px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <span className="text-2xl">✨</span>
              <h3 className="text-white text-xl font-semibold">
                Generate AI Project Image
              </h3>
            </div>
            <button 
              className="text-white/80 hover:text-white text-3xl transition-colors"
              onClick={onClose}
            >
              ×
            </button>
          </div>
          <p className="text-white/80 text-sm mt-1">
            {projectData?.frond?.title || 'Loading...'} • {projectData?.frond?.category || 'General'}
          </p>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {generatedImages.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🎨</div>
              <p className="text-gray-600 mb-6">
                Click the button below to generate AI images for your project
              </p>
              <button
                className="bg-gradient-to-r to-orange-600 from-pink-500 text-white px-8 py-3 rounded-full font-semibold hover:shadow-lg transition-all disabled:opacity-50"
                onClick={generateImages}
                disabled={isGenerating}
              >
                {isGenerating ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                    </svg>
                    Generating...
                  </span>
                ) : (
                  '✨ Generate Images'
                )}
              </button>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-500 mb-4">
                Click on any image to set it as your project cover
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {generatedImages.map((image, idx) => (
                  <div
                    key={idx}
                    className={`relative group cursor-pointer rounded-xl overflow-hidden border-2 transition-all ${
                      selectedImage?.url === image.url 
                        ? 'border-orange-500 shadow-lg scale-105' 
                        : 'border-transparent hover:scale-105'
                    }`}
                    onClick={() => selectAsCover(image)}
                  >
                    <img
                      src={image.thumb}
                      alt={image.keyword}
                      className="w-full h-40 object-cover"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="text-white text-sm font-medium">Select as Cover</span>
                    </div>
                    {selectedImage?.url === image.url && (
                      <div className="absolute top-2 right-2 bg-green-500 rounded-full p-1">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                      <p className="text-white text-xs truncate">{image.keyword}</p>
                      <p className="text-white/70 text-[10px] truncate">📷 {image.author}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-center mt-6">
                <button
                  className="text-sm text-orange-600 hover:text-orange-700 underline"
                  onClick={generateImages}
                  disabled={isGenerating}
                >
                  {isGenerating ? 'Generating...' : '🔄 Regenerate images'}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
          {saving && (
            <div className="flex items-center gap-2 text-orange-600">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
              <span>Saving image...</span>
            </div>
          )}
          <button
            className="px-6 py-2 rounded-lg font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-all"
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(30px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
        .animate-slideUp { animation: slideUp 0.4s ease-out; }
      `}</style>
    </div>
  );
};

export default AIGenerateImageModal;